// src/pages/admin/AdminCreateHero.jsx
import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import AllHero from "./AllHero";

const AdminCreateHero = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const token = Cookies.get("gameport");
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    isActive: true,
  });

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");
  const [errors, setErrors] = useState({});

  // ✅ cleanup preview object URL (prevents memory leak)
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const { mutate, isPending, isError, error } = useMutation({
    mutationKey: ["hero-create"],
    mutationFn: async () => {
      if (!token) throw new Error("You are not authenticated.");

      const fd = new FormData();
      fd.append("title", formData.title.trim());
      fd.append("subtitle", formData.subtitle.trim());
      fd.append("isActive", String(formData.isActive));
      if (image) fd.append("image", image);

      const res = await fetch(`${API_URL}/admin/hero/create`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(result.message || "Failed to create hero");
      return result;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hero-active"] });
      qc.invalidateQueries({ queryKey: ["hero-all"] }); // ✅ if you have list query
      navigate("/");
    },
  });

  const validate = () => {
    const e = {};
    if (!formData.title.trim()) e.title = "Title is required";
    if (!image) e.image = "Hero image is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    mutate();
  };

  const inputBase =
    "w-full mt-1 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-gray-800 placeholder:text-gray-400 " +
    "focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300";

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-8 sm:py-10">
        {/* Card */}
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-7 md:p-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">
            Create Hero
          </h2>
          <p className="text-center text-sm sm:text-base text-gray-500 mt-1 mb-7 sm:mb-8">
            Set homepage hero title, text and background
          </p>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-5 sm:gap-6">
            {/* Title */}
            <div>
              <label className="text-sm font-medium text-gray-700">Title</label>
              <input
                value={formData.title}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, title: e.target.value }))
                }
                className={inputBase}
                placeholder="e.g. Buy Games & Gift Cards"
              />
              {errors.title && (
                <p className="mt-1 text-xs text-red 600">{errors.title}</p>
              )}
            </div>

            {/* Subtitle */}
            <div>
              <label className="text-sm font-medium text-gray-700">Subtitle</label>
              <textarea
                rows={4}
                value={formData.subtitle}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, subtitle: e.target.value }))
                }
                className={inputBase}
                placeholder="Short supporting text..."
              />
            </div>

            {/* Active toggle (optional but useful) */}
            <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-gray-800">Set as active</p>
                <p className="text-xs text-gray-500">
                  Active hero will display on the homepage.
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setFormData((p) => ({ ...p, isActive: !p.isActive }))
                }
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                  formData.isActive ? "bg-red 600" : "bg-gray-300"
                }`}
                aria-pressed={formData.isActive}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                    formData.isActive ? "translate-x-5" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {/* File input */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Hero Background Image
              </label>

              <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4">
                <input
                  type="file"
                  accept="image/*"
                  className="block w-full text-sm text-gray-700
                    file:mr-4 file:rounded-lg file:border-0
                    file:bg-gray-900 file:px-4 file:py-2 file:text-white
                    hover:file:opacity-90"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;

                    // cleanup old preview
                    if (preview) URL.revokeObjectURL(preview);

                    setImage(f);
                    setPreview(URL.createObjectURL(f));
                    setErrors((p) => ({ ...p, image: undefined }));
                  }}
                />

                <p className="mt-2 text-xs text-gray-500">
                  Recommended: 1600×600 (or similar wide banner).
                </p>

                {errors.image && (
                  <p className="mt-1 text-xs text-red 600">{errors.image}</p>
                )}
              </div>
            </div>

            {/* Preview */}
            {preview && (
              <div className="rounded-2xl overflow-hidden border border-gray-200">
                <img
                  src={preview}
                  alt="preview"
                  className="w-full object-cover h-44 sm:h-56 md:h-64"
                />
              </div>
            )}

            {/* Error */}
            {isError && <p className="text-red 600 text-sm">{error?.message}</p>}

            {/* Submit */}
            <button
              type="submit"
              disabled={isPending}
              className={`w-full py-3 rounded-xl font-semibold transition shadow-sm ${
                isPending
                  ? "bg-gray-300 text-gray-700 cursor-not-allowed"
                  : "bg-red 600 hover:bg-red 700 active:bg-red 800 text-white"
              }`}
            >
              {isPending ? "Creating..." : "Create Hero"}
            </button>
          </form>
        </div>

        {/* List / existing heroes */}
        <div className="mt-8">
          <AllHero />
        </div>
      </div>
    </div>
  );
};

export default AdminCreateHero;
